import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import {
  toEncounterSheet,
  type EncounterCandidate,
  type EncounterSheet,
  type EncounterSummary,
} from './types'

type Schema = components['schemas']

/**
 * Query keys.
 *
 * Two entries per encounter, on the same split as scenes and campaigns: the sheet
 * (core + sides) and the cards (core + every participant's full combat card). The cards
 * are a separate read because each one is an entire character sheet.
 */
export const encounterKeys = {
  all: ['encounters'] as const,
  lists: () => [...encounterKeys.all, 'list'] as const,
  list: (campaignId: number, page: number, pageSize: number) =>
    [...encounterKeys.lists(), campaignId, page, pageSize] as const,
  sheets: () => [...encounterKeys.all, 'sheet'] as const,
  sheet: (encounterId: number) => [...encounterKeys.sheets(), encounterId] as const,
  cardSets: () => [...encounterKeys.all, 'cards'] as const,
  cards: (encounterId: number) => [...encounterKeys.cardSets(), encounterId] as const,
  candidates: (encounterId: number) =>
    [...encounterKeys.all, encounterId, 'available-characters'] as const,
}

export interface EncounterListPage {
  items: EncounterSummary[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

/**
 * A campaign's encounters.
 *
 * **Game Master only, reads included** — a card is every NPC's full sheet, so the API
 * gives a player 403 rather than an empty list. The UI must not show players an entry
 * point at all; this query is only ever mounted behind that check.
 */
export function useEncounterList(
  campaignId: number,
  enabled = true,
  page = 1,
  pageSize = 50,
): UseQueryResult<EncounterListPage> {
  return useQuery({
    queryKey: encounterKeys.list(campaignId, page, pageSize),
    enabled: enabled && Number.isFinite(campaignId),
    queryFn: async () => {
      const result = await api.get<Schema['EncounterSummaryDtoPagedResult']>(
        `/campaigns/${campaignId}/encounters`,
        { query: { page, pageSize } },
      )

      return {
        items: result.items ?? [],
        page: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        totalCount: result.totalCount ?? 0,
        totalPages: result.totalPages ?? 0,
      }
    },
  })
}

/** The encounter sheet: core and its sides. */
export function useEncounterSheet(encounterId: number): UseQueryResult<EncounterSheet> {
  return useQuery({
    queryKey: encounterKeys.sheet(encounterId),
    enabled: Number.isFinite(encounterId),
    queryFn: async () =>
      toEncounterSheet(await api.get<Schema['EncounterSheetDto']>(`/encounters/${encounterId}`)),
  })
}

/**
 * Every participant's combat card.
 *
 * Opt-in, like the scene snapshot: this is N full character sheets and most encounter
 * opens are about arranging the sides rather than reading them.
 */
export function useEncounterCards(
  encounterId: number,
  enabled: boolean,
): UseQueryResult<EncounterSheet> {
  return useQuery({
    queryKey: encounterKeys.cards(encounterId),
    enabled: enabled && Number.isFinite(encounterId),
    queryFn: async () =>
      toEncounterSheet(
        await api.get<Schema['EncounterSheetDto']>(`/encounters/${encounterId}/cards`),
      ),
  })
}

/** Roster characters not yet on any side. Server-filtered. */
export function useEncounterCandidates(
  encounterId: number,
  enabled = true,
): UseQueryResult<EncounterCandidate[]> {
  return useQuery({
    queryKey: encounterKeys.candidates(encounterId),
    enabled: enabled && Number.isFinite(encounterId),
    queryFn: () => api.get<EncounterCandidate[]>(`/encounters/${encounterId}/available-characters`),
  })
}

export interface CreateEncounterInput {
  name: string
  type: Schema['EncounterType']
  /** Omitted, the API creates its two default sides. */
  sideNames?: string[] | undefined
  /**
   * Where the fight happens. Optional, because an encounter is usually planned before anyone
   * knows — and it is what a live session later runs on.
   */
  locationId?: number | null | undefined
}

export function useCreateEncounter(campaignId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateEncounterInput) =>
      toEncounterSheet(
        await api.post<Schema['EncounterSheetDto']>(`/campaigns/${campaignId}/encounters`, {
          name: input.name,
          type: input.type,
          sideNames: input.sideNames ?? null,
          locationId: input.locationId ?? null,
        }),
      ),

    onSuccess: (sheet) => {
      void queryClient.invalidateQueries({ queryKey: encounterKeys.lists() })
      queryClient.setQueryData(encounterKeys.sheet(sheet.encounterId), sheet)
    },
  })
}

export function useDeleteEncounter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (encounterId: number) => api.delete<void>(`/encounters/${encounterId}`),

    onSuccess: (_result, encounterId) => {
      queryClient.removeQueries({ queryKey: encounterKeys.sheet(encounterId) })
      queryClient.removeQueries({ queryKey: encounterKeys.cards(encounterId) })
      void queryClient.invalidateQueries({ queryKey: encounterKeys.lists() })
    },
  })
}
